import { observer } from 'mobx-react-lite'
import { CheckCircle, EditPencil, Trash } from 'iconoir-react'
import { useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeader } from 'app/components/ui/page-header'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { useTemplateTabs } from 'app/components/templates/useTemplateTabs'
import type { TaskTemplate } from 'app/generated/graphql'
import { writeFallbacks } from 'app/lib/workflows/messageFallbacks'
import { TaskTemplateEditorDialog } from './TaskTemplateEditorDialog'
import type { UseTaskTemplatesReturn } from './useTaskTemplates'

export const TaskTemplatesShadcnComponent = observer(function TaskTemplatesShadcnComponent({
  templates,
  saving,
  staffOptions,
  staffLoading,
  setSearch,
  editing,
  openNew,
  openEdit,
  closeEditor,
  saveTemplate,
  removeTemplate,
}: UseTaskTemplatesReturn) {
  const { tabs } = useTemplateTabs()
  const staffName = useMemo(() => new Map(staffOptions.map((s) => [s.id, s.name])), [staffOptions])
  const columns = useMemo<ColumnDef<TaskTemplate>[]>(
    () => [
      {
        id: `name`,
        accessorKey: `name`,
        header: `Name`,
        cell: (props) => <span className="font-medium">{props.getValue<string>()}</span>,
        meta: { defaultVisible: true },
      },
      {
        id: `title`,
        accessorKey: `title`,
        header: `Task title`,
        cell: (props) => (
          <span className="line-clamp-2 max-w-xl text-subtle">{writeFallbacks(props.getValue<string>(), {})}</span>
        ),
        meta: { defaultVisible: true },
      },
      {
        id: `due`,
        accessorKey: `dueInDays`,
        header: `Due`,
        cell: (props) => {
          const days = props.getValue<number | undefined>()
          return days != null ? `${days} ${days === 1 ? `day` : `days`} after applying` : `—`
        },
        meta: { defaultVisible: true },
      },
      {
        id: `owner`,
        accessorKey: `ownerId`,
        header: `Owner`,
        cell: (props) => {
          const id = props.getValue<string | undefined>()
          if (!id) return `—`
          // While staff are loading, a quiet dash beats a false "Former staff".
          if (staffLoading) return `—`
          return staffName.get(id) ?? `Former staff`
        },
        meta: { defaultVisible: true },
      },
      {
        id: `updated`,
        accessorKey: `updatedDate`,
        header: `Updated`,
        cell: (props) => moment(props.getValue<string>()).format(`MMM D, YYYY`),
        meta: { defaultVisible: true },
      },
      {
        id: `actions`,
        header: ``,
        cell: (props) => (
          // stopPropagation: the row itself opens the editor, so a click here
          // must not also fire that (it would reopen the editor behind a
          // delete, or open it twice).
          <div className="flex items-center gap-3">
            <TooltipWrapper message="Edit template">
              <EditPencil
                className="h-4 w-4 cursor-pointer text-subtle hover:text-default"
                onClick={(e) => {
                  e.stopPropagation()
                  openEdit(props.row.original)
                }}
              />
            </TooltipWrapper>
            <TooltipWrapper message="Delete template">
              <Trash
                className="h-4 w-4 cursor-pointer text-red-500 hover:text-red-700"
                onClick={(e) => {
                  e.stopPropagation()
                  removeTemplate(props.row.original)
                }}
              />
            </TooltipWrapper>
          </div>
        ),
        meta: { defaultVisible: true },
      },
    ],
    [openEdit, removeTemplate, staffName, staffLoading],
  )

  return (
    <>
      <PageScroll variant="table" header={<PageHeader title="Templates" tabs={tabs} />}>
        {/* pt-6 = the house gap between a tab underline and the toolbar row
            (matches Mapping's tab children and the SMS tab). */}
        <div className="h-full pt-6">
          <SimpleTable
            columns={columns}
            data={templates}
            hideFooter
            // Opening a template is the primary action, so the whole row is the
            // target. The Edit pencil is the last column of a wide table; on a
            // phone it sits behind a long horizontal scroll.
            meta={{ onRowClick: openEdit }}
            emptyState={
              <TableEmptyState
                icon={<CheckCircle className="h-6 w-6" strokeWidth={1.5} />}
                title="No task templates yet"
                description="Save the tasks your team creates over and over, like broken-appointment follow-ups and insurance verifications. Set a due date and owner once, then apply them from any task form or a workflow."
                action={
                  <Button variant="primary" size="sm" onClick={openNew}>
                    New Template
                  </Button>
                }
              />
            }
            edgePadded
            onSearchValueChange={setSearch}
            // Tabbed page: the primary action sits beside search, not in the
            // header, same layout as Mapping's tab children.
            extraSlot={
              <Button variant="primary" size="sm" onClick={openNew}>
                New Template
              </Button>
            }
          />
        </div>
      </PageScroll>
      <TaskTemplateEditorDialog
        open={!!editing}
        template={editing === `new` ? undefined : editing}
        staffOptions={staffOptions}
        saving={saving}
        onSave={saveTemplate}
        onCancel={closeEditor}
      />
    </>
  )
})
